import { GoogleGenAI } from "@google/genai";

import { EvaluationScale } from "./scalesService";

interface GeneratedQuestions {
    selfEvaluationQuestion: string;
    leaderEvaluationQuestion: string;
    suggestedScaleId?: string;
}

export class CriteriaAIService {
    private genAI: GoogleGenAI | null = null;
    private apiKey: string | undefined;

    constructor() {
        this.apiKey = import.meta.env.VITE_REACT_APP_GEMINI_API_KEY || import.meta.env.VITE_REACT_GEMINI_API_KEY;
        if (this.apiKey) {
            this.genAI = new GoogleGenAI({ apiKey: this.apiKey });
        } else {
            console.warn("Gemini API Key missing");
        }
    }

    async generateIndicatorQuestions(indicatorDescription: string, scales: EvaluationScale[] = []): Promise<GeneratedQuestions> {
        if (!this.genAI) {
            throw new Error("Gemini API não configurada.");
        }

        const scalesContext = scales.length > 0
            ? `
            ESCALAS DISPONÍVEIS:
            ${scales.map(s => `- ID: ${s.id} | Nome: ${s.name} | Descrição: ${s.description}`).join('\n')}
            
            Com base no indicador fornecido, SUGIRA o ID da escala mais apropriada dentre as listadas acima.
            Se nenhuma se encaixar perfeitamente, escolha a que for mais genérica ou padrão (ex: 1-5).
            `
            : '';

        const prompt = `
      Atue como um especialista em RH e gestão de desempenho.
      Dado o seguinte indicador de desempenho: "${indicatorDescription}"
      
      ${scalesContext}

      Gere duas perguntas para serem usadas em uma avaliação de desempenho:
      1. Uma pergunta para AUTOAVALIAÇÃO (direcionada ao colaborador, usando "você").
      2. Uma pergunta para AVALIAÇÃO DO LÍDER (direcionada ao líder sobre o colaborador).

      As perguntas devem seguir o estilo do exemplo abaixo, expandindo o entendimento do indicador e explicando brevemente o impacto ou o contexto esperado.
      
      EXEMPLO DE REFERÊNCIA (USE ESTE PADRÃO):
      Indicador: "Reporta com antecedência erros ou impedimentos"
      Pergunta Autoavaliação: "Com que frequência você identifica e reporta erros ou impedimentos com antecedência suficiente para que ações corretivas sejam tomadas, evitando ou minimizando impactos negativos?"
      Pergunta Líder: "O colaborador demonstra proatividade em identificar e comunicar erros ou impedimentos com antecedência, permitindo a tomada de ações corretivas e prevenindo impactos maiores?"

      Diretrizes:
      1. Evite perguntas genéricas e repetitivas como "Em que medida você realiza [indicador]?".
      2. Enriqueça a pergunta explicando O PORQUÊ aquele comportamento é importante ou COMO ele deve ser manifestado (ex: "para evitar impactos negativos").
      3. Mantenha o tom profissional e educativo, guiando o avaliador.
      4. NÃO peça exemplos específicos (ex: "cite um caso"), foque na constância e qualidade da atitude.
      
      Retorne APENAS um JSON no seguinte formato:
      {
        "selfEvaluationQuestion": "...",
        "leaderEvaluationQuestion": "...",
        "suggestedScaleId": "ID_DA_ESCALA_SUGERIDA (ou null se não houver escalas)"
      }
    `;

        try {
            const response = await this.genAI.models.generateContent({
                model: "gemini-2.5-flash",
                contents: [
                    {
                        role: "user",
                        parts: [{ text: prompt }]
                    }
                ],
                config: {
                    responseMimeType: "application/json"
                }
            });

            const text = response.text;

            if (!text) {
                throw new Error("Nenhuma resposta gerada.");
            }

            return JSON.parse(text);

        } catch (error) {
            console.error("Error generating questions:", error);
            throw error;
        }
    }
}

export const criteriaAIService = new CriteriaAIService();
